import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DevHubBox,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubTypography,
  DevHubAlert,
  DevHubGrid,
  DevHubLink,
  DevHubIconButton,
  DevHubTooltip,
  devhubMakeStyles,
  type DevHubTheme,
} from '@devxdiscover/devhub-ui';
import {
  GetCloudAccountsCountQuery,
  GetAdGroupsCountQuery,
  GetIamClientsCountQuery,
  GetApplicationComponentsCountQuery,
  GetDeviceResourceCountsQuery,
  ApplicationResourceCounts,
  initialResourceCounts,
  createChecklistItems,
  GetRepositoryResourceCountsQuery,
  ResourceErrorState,
  initialResourceErrors,
  fetchCnapResourcesCount,
  GetSatCountsQuery,
  DeleteApplicationAdGroupMutation,
  type ApplicationRoleEntry,
  type ResourceChecklistItem,
  resourceGroups,
} from '../queries/ResourceCountsQueries';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { IconChoices } from '@devxdiscover/devhub-ui/dist/components/icons/IconProps';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { ConfirmModal } from '../../common/ConfirmModal';

const useStyles = devhubMakeStyles((theme: DevHubTheme) => ({
  // Background colors
  successBackground: {
    backgroundColor: `${theme.palette.success.main}12`,
  },
  warningBackground: {
    backgroundColor: `${theme.palette.warning.main}14`,
  },
  errorBackground: {
    backgroundColor: `${theme.palette.error.main}14`,
  },
  // Border colors
  successBorder: {
    border: `1px solid ${theme.palette.success.main}80`,
  },
  warningBorder: {
    border: `1px solid ${theme.palette.warning.main}4D`,
  },
  errorBorder: {
    border: `1px solid ${theme.palette.error.main}4D`,
  },
  // Text/icon colors
  successColor: {
    color: theme.palette.success.main,
  },
  warningColor: {
    color: theme.palette.warning.main,
  },
  errorColor: {
    color: theme.palette.error.main,
  },
  infoColor: {
    color: theme.palette.info.main,
  },
  // Section headers
  mandatorySectionHeader: {
    borderBottom: `2px solid ${theme.palette.warning.main}`,
    paddingBottom: theme.spacing(1),
  },
  informationalSectionHeader: {
    borderBottom: `2px solid ${theme.palette.info.main}`,
    paddingBottom: theme.spacing(1),
  },
  resourceCard: {
    borderRadius: 8,
    transition: 'all 0.2s ease',
  },
  iconContainer: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconImage: {
    width: 40,
    height: 40,
    objectFit: 'contain' as const,
  },
  readonlyIndicator: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '2px solid currentColor',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  readonlyIndicatorChecked: {
    backgroundColor: 'currentColor',
  },
  readonlyIndicatorCheck: {
    color: '#fff',
    fontSize: 12,
    lineHeight: 1,
  },
  expandedItemsList: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    maxHeight: 320,
    overflowY: 'auto' as const,
  },
  cardDivider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  cardHeaderClickable: {
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  collapseAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    userSelect: 'none' as const,
    marginTop: theme.spacing(1),
  },
}));

interface ResourceCountsChecklistProps {
  applicationId: number;
  applicationName?: string;
  /** Callback when all resource counts are zero (ready for retirement) */
  onAllResourcesCleared?: (allCleared: boolean) => void;
  /** Callback when loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
}

type ItemValidationStatus = 'ok' | 'warning' | 'error';
type SectionSeverity = 'success' | 'warning' | 'critical';

type SectionSummary = {
  totalItems: number;
  itemsRequiringAction: number;
  isComplete: boolean;
  severity: SectionSeverity;
};

const getItemValidationStatus = (
  item: ResourceChecklistItem,
): ItemValidationStatus => {
  if (item.hasError) {
    return 'error';
  }
  if (item.count > 0) {
    return 'warning';
  }
  return 'ok';
};

const getSectionSummary = (items: ResourceChecklistItem[]): SectionSummary => {
  const statuses = items.map(getItemValidationStatus);
  const itemsRequiringAction = statuses.filter(
    status => status !== 'ok',
  ).length;
  const hasError = statuses.includes('error');
  const hasWarning = statuses.includes('warning');

  let severity: SectionSeverity = 'success';
  if (hasError) {
    severity = 'critical';
  } else if (hasWarning) {
    severity = 'warning';
  }

  return {
    totalItems: items.length,
    itemsRequiringAction,
    isComplete: items.length > 0 && itemsRequiringAction === 0,
    severity,
  };
};

const getStatusSymbol = (status: ItemValidationStatus): string => {
  if (status === 'error') {
    return '✘';
    // return '✗';
  }
  if (status === 'warning') {
    return '⚠';
  }
  return '✔';
};

const getSeverityFromStatus = (
  status: ItemValidationStatus,
): SectionSeverity => {
  if (status === 'error') {
    return 'critical';
  }
  if (status === 'warning') {
    return 'warning';
  }
  return 'success';
};

const getSummaryText = (summary: SectionSummary): string => {
  if (summary.itemsRequiringAction === 0) {
    return '✔ All items are valid';
  }
  const count = summary.itemsRequiringAction;
  const label = count === 1 ? 'item requires' : 'items require';
  return `⚠ ${count} ${label} action`;
};

/**
 * Component to display resource counts for an application before retirement.
 * Fetches and displays counts for cloud accounts, devices, AD groups,
 * IAM SSO clients, GitHub repos, CNAP resources, and components.
 * Progress is automatically tracked based on which resources have a count of 0.
 *
 * @param props - Component props
 * @param props.applicationId - The application ID to fetch resource counts for
 * @param props.applicationName - Optional application name for display
 * @param props.onAllResourcesCleared - Callback when all resource counts are zero
 */
export const ResourceCountsChecklist = ({
  applicationId,
  applicationName,
  onAllResourcesCleared,
  onLoadingChange,
}: ResourceCountsChecklistProps) => {
  const classes = useStyles();
  const [resourceCounts, setResourceCounts] =
    useState<ApplicationResourceCounts>(initialResourceCounts);
  const [resourceErrors, setResourceErrors] = useState<ResourceErrorState>(
    initialResourceErrors,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [completedQueries, setCompletedQueries] = useState<number>(0);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [adGroupDeleteModalOpen, setAdGroupDeleteModalOpen] = useState(false);
  const [adGroupDeleteError, setAdGroupDeleteError] = useState<
    string | undefined
  >(undefined);
  const [adGroupsList, setAdGroupsList] = useState<
    { groupName: string; groupId: string }[]
  >([]);
  const [adGroupRolesRaw, setAdGroupRolesRaw] = useState<
    ApplicationRoleEntry[]
  >([]);
  const TOTAL_QUERIES = 8; // cloud, ad, iam, devices, repos, components, cnap, sat (combined)
  const config = useApi(configApiRef);
  const baseUrl = config.getString('app.baseUrl');
  const applicationRolesUrl = `/catalog/default/system/${applicationId}/application-roles`;

  const [deleteAdGroups, { loading: deletingAdGroups }] = useMutation(
    DeleteApplicationAdGroupMutation,
  );

  /** Map of AD group name to role displayNames assigned to that group. */
  const adGroupRolesMap = useMemo<Record<string, string[]>>(() => {
    const adGroupNamesSet = new Set(adGroupsList.map(g => g.groupName));
    const map: Record<string, string[]> = {};
    adGroupRolesRaw.forEach(role => {
      role.responsibilities?.forEach(resp => {
        const groupName = resp.responsibleParty?.name;
        if (groupName && adGroupNamesSet.has(groupName)) {
          if (!map[groupName]) map[groupName] = [];
          if (!map[groupName].includes(role.displayName)) {
            map[groupName].push(role.displayName);
          }
        }
      });
    });
    return map;
  }, [adGroupRolesRaw, adGroupsList]);

  // Helper to mark a query as complete
  const markQueryComplete = useCallback(() => {
    setCompletedQueries(prev => prev + 1);
  }, []);

  // Update loading state when all queries complete
  useEffect(() => {
    if (completedQueries >= TOTAL_QUERIES) {
      setIsLoading(false);
    }
  }, [completedQueries]);

  // Query for cloud accounts count
  const [fetchCloudAccountsCount] = useLazyQuery(GetCloudAccountsCountQuery, {
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const cloudAccountsCount =
        data?.resources?.application?.cloudAccounts?.length || 0;
      setResourceCounts(prev => ({
        ...prev,
        cloudAccountsCount,
      }));
      markQueryComplete();
    },
    onError() {
      setResourceErrors(prev => ({
        ...prev,
        cloudAccountsCount: true,
      }));
      markQueryComplete();
    },
  });

  // Query for AD groups count
  const [fetchAdGroupsCount] = useLazyQuery(GetAdGroupsCountQuery, {
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const adGroupsCount =
        data?.resources?.application?.resources?.adGroupsPaged?.totalCount || 0;
      const groups =
        data?.resources?.application?.resources?.adGroupsPaged?.adGroups || [];
      const roles = data?.resources?.application?.roles || [];
      setResourceCounts(prev => ({
        ...prev,
        adGroupsCount,
      }));
      setAdGroupsList(groups);
      setAdGroupRolesRaw(roles);
      markQueryComplete();
    },
    onError() {
      setResourceErrors(prev => ({
        ...prev,
        adGroupsCount: true,
      }));
      if (adGroupDeleteModalOpen) {
        setAdGroupDeleteError(
          'Failed to re-check role assignments. Please close and try again.',
        );
      }
      markQueryComplete();
    },
  });

  // Query for IAM SSO clients count
  const [fetchIamClientsCount] = useLazyQuery(GetIamClientsCountQuery, {
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const iamClientsCount =
        data?.resources?.application?.resources?.ssoClientsPaged?.totalCount ||
        0;
      setResourceCounts(prev => ({
        ...prev,
        iamClientsCount,
      }));
      markQueryComplete();
    },
    onError() {
      setResourceErrors(prev => ({
        ...prev,
        iamClientsCount: true,
      }));
      markQueryComplete();
    },
  });

  // Query for device resource counts
  const [fetchDeviceResourceCounts] = useLazyQuery(
    GetDeviceResourceCountsQuery,
    {
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        const devicesCount =
          data?.resources?.application?.devicesPaged?.totalCount || 0;
        setResourceCounts(prev => ({
          ...prev,
          devicesCount,
        }));
        markQueryComplete();
      },
      onError() {
        setResourceErrors(prev => ({
          ...prev,
          devicesCount: true,
        }));
        markQueryComplete();
      },
    },
  );

  // Query for repository resource counts
  const [fetchRepositoryResourceCounts] = useLazyQuery(
    GetRepositoryResourceCountsQuery,
    {
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        const repositoriesCount =
          data?.resources?.application?.sourceControl?.github?.repositoriesPaged
            ?.totalCount || 0;
        setResourceCounts(prev => ({
          ...prev,
          githubReposCount: repositoriesCount,
        }));
        markQueryComplete();
      },
      onError() {
        setResourceErrors(prev => ({
          ...prev,
          githubReposCount: true,
        }));
        markQueryComplete();
      },
    },
  );

  // Query for components count (uses different filter)
  const [fetchComponentsCount] = useLazyQuery(
    GetApplicationComponentsCountQuery,
    {
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        const componentsCount =
          data?.resources?.application?.componentsPaged?.totalCount || 0;
        setResourceCounts(prev => ({
          ...prev,
          componentsCount,
        }));
        markQueryComplete();
      },
      onError() {
        setResourceErrors(prev => ({
          ...prev,
          componentsCount: true,
        }));
        markQueryComplete();
      },
    },
  );

  // Combined query for SAT clients and SAT services counts
  const [fetchSatCounts] = useLazyQuery(GetSatCountsQuery, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      const satClientsCount =
        data?.resources?.application?.resources?.satClientsPaged?.totalCount ||
        0;
      const satServicesCount =
        data?.resources?.application?.resources?.satServicesPaged?.totalCount ||
        0;
      setResourceCounts(prev => ({
        ...prev,
        satClientsCount,
        satServicesCount,
      }));
      markQueryComplete();
    },
    onError() {
      setResourceErrors(prev => ({
        ...prev,
        satClientsCount: true,
        satServicesCount: true,
      }));
      markQueryComplete();
    },
  });

  // Notify parent when MANDATORY resources are cleared (ready for retirement)
  // Only mandatory items (Cloud Accounts, Devices, IAM SSO Clients) must be 0
  // Informational items don't block retirement
  useEffect(() => {
    const items = createChecklistItems(resourceCounts, resourceErrors);
    const mandatoryItems = items.filter(item => item.isMandatory);
    const allMandatoryCleared = getSectionSummary(mandatoryItems).isComplete;
    onAllResourcesCleared?.(allMandatoryCleared);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceCounts, resourceErrors, applicationId]);

  // Notify parent when loading state changes
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Fetch CNAP resources count via REST API
  const fetchAndSetCnapResourcesCount = useCallback(async () => {
    const result = await fetchCnapResourcesCount(baseUrl, applicationId);
    setResourceCounts(prev => ({
      ...prev,
      cnapResourcesCount: result.count,
    }));
    if (result.errorOccurred) {
      setResourceErrors(prev => ({
        ...prev,
        cnapResourcesCount: true,
      }));
    }
    markQueryComplete();
  }, [baseUrl, applicationId, markQueryComplete]);

  // Fetch resource counts when component mounts or applicationId changes
  useEffect(() => {
    if (applicationId && applicationId > 0) {
      setIsLoading(true);
      setCompletedQueries(0);
      setResourceErrors(initialResourceErrors);
      setExpandedSections({});

      // Fetch cloud accounts count
      fetchCloudAccountsCount({
        variables: {
          applicationId,
          filter: {
            cloudAccountStatuses: ['active'],
          },
        },
      });

      // Fetch AD groups count
      fetchAdGroupsCount({
        variables: { applicationId },
      });

      // Fetch IAM SSO clients count
      fetchIamClientsCount({
        variables: {
          applicationId,
        },
      });

      // Fetch device counts
      fetchDeviceResourceCounts({
        variables: {
          applicationId,
          filter: {
            operationalStatus: 'OPERATIONAL',
          },
        },
      });

      // Fetch components count with application filter
      fetchComponentsCount({
        variables: {
          applicationId,
        },
      });

      // Fetch repository counts
      fetchRepositoryResourceCounts({
        variables: {
          applicationId,
          filter: {
            archived: false,
          },
        },
      });

      // Fetch SAT clients and services counts (combined query)
      fetchSatCounts({
        variables: {
          applicationId,
        },
      });

      // Fetch CNAP resources count via REST API
      fetchAndSetCnapResourcesCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  // Re-fetch AD groups + roles when the user returns from the Roles page
  // so the confirm button can re-evaluate after cleanup.
  // Only the window focus listener is needed — initial data is already loaded.
  useEffect(() => {
    if (!adGroupDeleteModalOpen) {
      return undefined;
    }

    const handleWindowFocus = () => {
      fetchAdGroupsCount({ variables: { applicationId } });
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupDeleteModalOpen, applicationId]);

  const checklistItems = createChecklistItems(resourceCounts, resourceErrors);
  const mandatoryItems = checklistItems.filter(item => item.isMandatory);
  const informationalItems = checklistItems.filter(item => !item.isMandatory);
  const mandatorySectionSummary = getSectionSummary(mandatoryItems);
  const informationalSectionSummary = getSectionSummary(informationalItems);

  const informationalWithResourcesCount =
    informationalSectionSummary.itemsRequiringAction;
  const errorCount = checklistItems.filter(item => item.hasError).length;

  const toggleSection = (sectionKey: string): void => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Helper functions - get class names based on state
  const getBackgroundClass = (severity: SectionSeverity): string => {
    if (severity === 'critical') return classes.errorBackground;
    if (severity === 'warning') return classes.warningBackground;
    return classes.successBackground;
  };

  const getBorderClass = (severity: SectionSeverity): string => {
    if (severity === 'critical') return classes.errorBorder;
    if (severity === 'warning') return classes.warningBorder;
    return classes.successBorder;
  };

  const getColorClass = (severity: SectionSeverity): string => {
    if (severity === 'critical') return classes.errorColor;
    if (severity === 'warning') return classes.warningColor;
    return classes.successColor;
  };

  const renderReadonlyIndicator = (
    isComplete: boolean,
    severity: SectionSeverity,
  ) => {
    const indicatorSeverity = isComplete ? 'success' : severity;

    return (
      <DevHubBox
        aria-hidden
        className={`${classes.readonlyIndicator} ${getColorClass(indicatorSeverity)} ${isComplete ? classes.readonlyIndicatorChecked : ''}`}
      >
        {isComplete && (
          <DevHubBox
            component="span"
            className={classes.readonlyIndicatorCheck}
          >
            ✓
          </DevHubBox>
        )}
      </DevHubBox>
    );
  };

  if (isLoading) {
    return (
      <DevHubBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <DevHubCircularProgress size={40} />
        <DevHubTypography variant="body2" style={{ marginTop: 16 }}>
          Loading resource counts...
        </DevHubTypography>
      </DevHubBox>
    );
  }

  // Check if all mandatory resources failed to load
  const allMandatoryFailed =
    mandatoryItems.length > 0 && mandatoryItems.every(item => item.hasError);

  if (allMandatoryFailed) {
    return (
      <DevHubAlert severity="error">
        Failed to load resource counts. Please try again.
      </DevHubAlert>
    );
  }

  // Helper to render a grouped card
  const renderGroupedCard = (
    groupTitle: string,
    groupIcon: IconChoices,
    groupImageSrc: string | undefined,
    groupDescription: string,
    items: (typeof checklistItems)[0][],
    allowDelete?: boolean,
  ) => {
    const cardSummary = getSectionSummary(items);
    const sectionKey = `${groupTitle.toLowerCase()}-group`;
    const isExpanded = expandedSections[sectionKey] || false;

    return (
      <DevHubGrid
        item
        xs={12}
        sm={6}
        key={sectionKey}
        style={{ display: 'flex' }}
      >
        <DevHubBox
          p={2}
          className={`${classes.resourceCard} ${getBackgroundClass(cardSummary.severity)} ${getBorderClass(cardSummary.severity)}`}
          style={{ width: '100%' }}
        >
          {/* Header row: icon + title + indicator */}
          <DevHubBox
            display="flex"
            alignItems="center"
            className={classes.cardHeaderClickable}
            onClick={() => toggleSection(sectionKey)}
            style={{ marginBottom: 6 }}
          >
            <DevHubBox mr={1.5} className={classes.iconContainer}>
              {groupImageSrc ? (
                <img
                  src={groupImageSrc}
                  alt={`${groupTitle} logo`}
                  className={classes.iconImage}
                />
              ) : (
                <DevHubDynamicIcon
                  iconName={groupIcon}
                  className={getColorClass(cardSummary.severity)}
                  style={{ fontSize: 28 }}
                />
              )}
            </DevHubBox>
            <DevHubTypography
              variant="h6"
              style={{ fontWeight: 700, flex: 1, marginTop: 2 }}
            >
              {groupTitle}
            </DevHubTypography>
            {renderReadonlyIndicator(
              cardSummary.isComplete,
              cardSummary.severity,
            )}
          </DevHubBox>

          {/* Summary */}
          <DevHubTypography
            variant="subtitle2"
            className={getColorClass(cardSummary.severity)}
            style={{ fontWeight: 600, marginBottom: 4 }}
          >
            {getSummaryText(cardSummary)}
          </DevHubTypography>

          {/* Description */}
          <DevHubTypography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginLeft: 12, minHeight: 32 }}
          >
            {groupDescription}
          </DevHubTypography>

          {/* Expand/Collapse action */}
          <DevHubBox
            className={classes.collapseAction}
            onClick={() => toggleSection(sectionKey)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <DevHubTypography
              variant="caption"
              weight="bold"
              style={{ fontWeight: 700 }}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </DevHubTypography>
            <DevHubDynamicIcon
              iconName="ExpandMore"
              style={{
                fontSize: 20,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </DevHubBox>

          {/* Expanded: divider + items */}
          {isExpanded && (
            <DevHubBox pl={2}>
              <DevHubBox className={classes.cardDivider} />
              <DevHubBox className={classes.expandedItemsList}>
                {items.map(item => {
                  const itemStatus = getItemValidationStatus(item);
                  const itemSeverity = getSeverityFromStatus(itemStatus);

                  return (
                    <DevHubBox key={item.key} mb={1.5}>
                      <DevHubTypography
                        variant="body2"
                        className={getColorClass(itemSeverity)}
                        style={{ fontWeight: 600 }}
                      >
                        {getStatusSymbol(itemStatus)} {item.label}
                      </DevHubTypography>
                      <DevHubTypography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', marginLeft: 20 }}
                      >
                        {item.hasError
                          ? 'Failed to load resource count'
                          : `${item.count} active item(s) found`}
                      </DevHubTypography>
                      <DevHubBox
                        ml={2.5}
                        mt={0.5}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <DevHubLink
                          href={item.actionUrl}
                          target={item.isExternal ? '_blank' : undefined}
                          rel={
                            item.isExternal ? 'noopener noreferrer' : undefined
                          }
                          style={{ fontSize: '0.8rem', fontWeight: 500 }}
                        >
                          {item.actionLabel}
                          {item.isExternal && (
                            <DevHubDynamicIcon
                              iconName="OpenInNew"
                              style={{
                                fontSize: 13,
                                marginLeft: 4,
                                verticalAlign: 'middle',
                              }}
                            />
                          )}
                        </DevHubLink>
                        {allowDelete &&
                          item.count > 0 &&
                          ConfigHelper.instance.isFeatureEnabled(
                            Feature.AD_GROUP_DELETE,
                          ) && (
                            <DevHubTooltip title="Initiate the AD group deletion process">
                              <DevHubIconButton
                                size="small"
                                onClick={e => {
                                  e.stopPropagation();
                                  setAdGroupDeleteError(undefined);
                                  setAdGroupDeleteModalOpen(true);
                                }}
                                aria-label="Delete AD groups"
                              >
                                <DevHubDynamicIcon
                                  iconName="Delete"
                                  style={{ fontSize: 20 }}
                                  color="red"
                                />
                              </DevHubIconButton>
                            </DevHubTooltip>
                          )}
                      </DevHubBox>
                    </DevHubBox>
                  );
                })}
              </DevHubBox>
            </DevHubBox>
          )}
        </DevHubBox>
      </DevHubGrid>
    );
  };

  return (
    <DevHubBox>
      {applicationName && (
        <DevHubTypography
          variant="h6"
          style={{ marginBottom: 16, fontWeight: 600 }}
        >
          Resources for: {applicationName}
        </DevHubTypography>
      )}

      {errorCount > 0 && (
        <DevHubAlert severity="error" style={{ marginBottom: 16 }}>
          <DevHubTypography variant="body2">
            Failed to load {errorCount} resource type(s). Please try refreshing
            the page or contact support if the issue persists.
          </DevHubTypography>
        </DevHubAlert>
      )}

      {/* MANDATORY SECTION */}
      <DevHubBox mb={3}>
        <DevHubBox
          display="flex"
          alignItems="center"
          mb={1}
          className={classes.mandatorySectionHeader}
        >
          <DevHubDynamicIcon
            iconName="PriorityHigh"
            className={classes.warningColor}
            style={{ fontSize: 20, marginRight: 8 }}
          />
          <DevHubTypography
            variant="subtitle1"
            className={classes.warningColor}
            style={{ fontWeight: 700 }}
          >
            Required for Retirement
          </DevHubTypography>
        </DevHubBox>
        <DevHubTypography
          variant="body2"
          color="textSecondary"
          className={getColorClass(mandatorySectionSummary.severity)}
          style={{ marginBottom: 6, fontWeight: 600 }}
        >
          {getSummaryText(mandatorySectionSummary)}
        </DevHubTypography>
        <DevHubTypography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: 12 }}
        >
          You must <strong>remove or disassociate</strong> all active resources
          below before you can retire this application. Click the links to
          manage each resource type.
        </DevHubTypography>
        <DevHubGrid container spacing={2}>
          {resourceGroups.map(group => {
            const groupItems = mandatoryItems.filter(
              item => item.groupId === group.groupId,
            );
            if (groupItems.length === 0) return null;
            return renderGroupedCard(
              group.title,
              group.icon as IconChoices,
              group.imageSrc,
              group.description,
              groupItems,
              group.allowDelete,
            );
          })}
          {/* Data Management Decommission Review - static guidance card */}
          <DevHubGrid
            item
            xs={12}
            sm={6}
            key="data-management-review"
            style={{ display: 'flex' }}
          >
            <DevHubBox
              p={2}
              className={`${classes.resourceCard} ${classes.warningBackground} ${classes.warningBorder}`}
              style={{ width: '100%' }}
            >
              <DevHubBox
                display="flex"
                alignItems="center"
                style={{ marginBottom: 6 }}
              >
                <DevHubBox mr={1.5} className={classes.iconContainer}>
                  <DevHubDynamicIcon
                    iconName="Security"
                    className={classes.warningColor}
                    style={{ fontSize: 28 }}
                  />
                </DevHubBox>
                <DevHubTypography
                  variant="h6"
                  style={{ fontWeight: 700, flex: 1, marginTop: 2 }}
                >
                  Data Management Decommission Review
                </DevHubTypography>
              </DevHubBox>
              <DevHubTypography
                variant="body2"
                style={{ marginBottom: 8, marginLeft: 12 }}
              >
                If this application stores data or has ever stored data, you are
                required to submit a JIRA Decommission ticket for review.
              </DevHubTypography>
              <DevHubBox ml={1.5}>
                <DevHubBox mb={0.5}>
                  <DevHubLink
                    href="https://tpx.sys.comcast.net/proforma/projects/CPPDR/create-issue?ac.form.id=67&ac.issuetype.id=10938"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.8rem', fontWeight: 500 }}
                  >
                    Create JIRA Decommission Ticket
                    <DevHubDynamicIcon
                      iconName="OpenInNew"
                      style={{
                        fontSize: 13,
                        marginLeft: 4,
                        verticalAlign: 'middle',
                      }}
                    />
                  </DevHubLink>
                </DevHubBox>
                <DevHubBox mb={0.5}>
                  <DevHubLink
                    href="https://docs-dx.comcast.com/kb/data-management/offboarding/data-decommission-guidelines/?keywords=data+decommission"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.8rem', fontWeight: 500 }}
                  >
                    Decommission Guidelines
                    <DevHubDynamicIcon
                      iconName="OpenInNew"
                      style={{
                        fontSize: 13,
                        marginLeft: 4,
                        verticalAlign: 'middle',
                      }}
                    />
                  </DevHubLink>
                </DevHubBox>
                <DevHubBox>
                  <DevHubLink
                    href="mailto:chq_dxedme_stewardship@comcast.com"
                    style={{ fontSize: '0.8rem', fontWeight: 500 }}
                  >
                    chq_dxedme_stewardship@comcast.com
                  </DevHubLink>
                </DevHubBox>
              </DevHubBox>
            </DevHubBox>
          </DevHubGrid>
        </DevHubGrid>
      </DevHubBox>

      {/* INFORMATIONAL SECTION */}
      <DevHubBox mb={3}>
        <DevHubBox
          display="flex"
          alignItems="center"
          mb={1}
          className={classes.informationalSectionHeader}
        >
          <DevHubDynamicIcon
            iconName="Info"
            className={classes.infoColor}
            style={{ fontSize: 20, marginRight: 8 }}
          />
          <DevHubTypography
            variant="subtitle1"
            className={classes.infoColor}
            style={{ fontWeight: 700 }}
          >
            Additional Resources
          </DevHubTypography>
        </DevHubBox>
        <DevHubTypography
          variant="body2"
          color="textSecondary"
          className={getColorClass(informationalSectionSummary.severity)}
          style={{ marginBottom: 6, fontWeight: 600 }}
        >
          {getSummaryText(informationalSectionSummary)}
        </DevHubTypography>
        <DevHubTypography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: 12 }}
        >
          These resources won’t block app retirement, but should be removed soon
          to avoid unnecessary resource usage. Use the links below to take
          action.
        </DevHubTypography>
        <DevHubGrid container spacing={2}>
          {resourceGroups.map(group => {
            const groupItems = informationalItems.filter(
              item => item.groupId === group.groupId,
            );
            if (groupItems.length === 0) return null;
            return renderGroupedCard(
              group.title,
              group.icon as IconChoices,
              group.imageSrc,
              group.description,
              groupItems,
              group.allowDelete,
            );
          })}
        </DevHubGrid>
      </DevHubBox>

      {/* Warning if informational items have resources */}
      {informationalWithResourcesCount > 0 && (
        <DevHubAlert severity="info" style={{ marginBottom: 16 }}>
          <DevHubTypography variant="body2">
            <strong>Action Required:</strong> You have{' '}
            {informationalWithResourcesCount} resource type(s) with active
            items. Use the links above to remove or archive them. We recommend
            addressing the remaining resources as soon as possible to ensure
            efficient resource usage.
          </DevHubTypography>
        </DevHubAlert>
      )}

      <DevHubBox mt={2}>
        <DevHubTypography variant="body2" color="textSecondary">
          <strong>Tip:</strong> A count of 0 means no active resources remain.
          Green items are complete and ready for retirement. Use the provided
          links to manage and remove active resources.
        </DevHubTypography>
      </DevHubBox>

      <ConfirmModal
        open={adGroupDeleteModalOpen}
        title="Delete AD Groups"
        message={
          <>
            You have selected to delete all{' '}
            <strong>{resourceCounts.adGroupsCount}</strong> AD group
            {resourceCounts.adGroupsCount !== 1 ? 's' : ''} associated with{' '}
            <strong>{applicationName || 'this application'}</strong>. Please
            confirm you understand and agree.
            {adGroupsList.length > 0 && (
              <ul style={{ margin: '12px 0 16px', paddingLeft: 20 }}>
                {adGroupsList.map(g => {
                  const roles = adGroupRolesMap[g.groupName] || [];
                  let rolesNode = null;
                  if (roles.length > 0) {
                    rolesNode = (
                      <span
                        className={classes.warningColor}
                        style={{ marginLeft: 8 }}
                      >
                        — roles: {roles.join(', ')}
                      </span>
                    );
                  }
                  return (
                    <li
                      key={g.groupId}
                      style={{ fontSize: '0.85rem', marginBottom: 4 }}
                    >
                      <strong>{g.groupName}</strong>
                      {rolesNode}
                    </li>
                  );
                })}
              </ul>
            )}
            {Object.keys(adGroupRolesMap).length > 0 && (
              <DevHubTypography
                variant="caption"
                className={classes.warningColor}
                style={{
                  display: 'block',
                  marginBottom: 8,
                }}
              >
                Some AD groups still have role assignments on this application.
                Clean them up on the{' '}
                <DevHubLink
                  href={`${baseUrl}${applicationRolesUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.infoColor}
                  style={{ fontWeight: 700 }}
                >
                  Roles page
                </DevHubLink>
                . After the assignments are removed, return here and the dialog
                will re-check before enabling the confirm button.
              </DevHubTypography>
            )}
          </>
        }
        warningMessage="This action cannot be undone. The groups will be removed from Active Directory. Please make sure you have cleared all associations with these AD Groups before proceeding."
        error={adGroupDeleteError}
        confirmLabel="I Understand & Agree"
        confirmIcon="Delete"
        isLoading={deletingAdGroups}
        variant="outlined"
        confirmTextColor="red"
        cancelButtonColor="white"
        confirmButtonColor="white"
        confirmDisabled={Object.keys(adGroupRolesMap).length > 0}
        onCancel={() => {
          setAdGroupDeleteModalOpen(false);
          setAdGroupDeleteError(undefined);
        }}
        onConfirm={async () => {
          setAdGroupDeleteError(undefined);
          try {
            const result = await deleteAdGroups({
              variables: { input: { applicationId } },
            });
            const outcome =
              result.data?.resources?.application?.deleteApplicationAdGroup;
            if (outcome?.success === false) {
              setAdGroupDeleteError(
                outcome.message || 'Deletion failed. Please try again.',
              );
              return;
            }
            setAdGroupDeleteModalOpen(false);
            fetchAdGroupsCount({ variables: { applicationId } });
          } catch (err) {
            setAdGroupDeleteError(
              err instanceof Error
                ? err.message
                : 'Deletion failed. Please try again.',
            );
          }
        }}
      />
    </DevHubBox>
  );
};

export default ResourceCountsChecklist;
